import {Component, DestroyRef, OnInit, inject, signal, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import { NgOptimizedImage } from '@angular/common';
import {environment} from "src/environments/environment";

import {OnboardingStep1Component} from "./onboarding-step-1/onboarding-step-1.component";
import {OnboardingStep2Component} from "./onboarding-step-2/onboarding-step-2.component";
import {animate, style, transition, trigger} from "@angular/animations";
import {OnboardingStep3Component} from "./onboarding-step-3/onboarding-step-3.component";
import {OnboardingStep4Component} from "./onboarding-step-4/onboarding-step-4.component";
import {OnboardingStep5Component} from "./onboarding-step-5/onboarding-step-5.component";
import {OnboardingStep6Component} from "./onboarding-step-6/onboarding-step-6.component";
import {FormControl, FormGroup} from "@angular/forms";
import {CurrentTenantStore} from "src/app/services/tenant/current-tenant.store";
import {TenantService} from "src/app/services/tenant/tenant.service";
import {PosthogService} from "src/app/services/analytics/posthog.service";
import {Router} from "@angular/router";
import {APPLICATIONS_URL, HOME_URL} from "src/app/models/home/navigation.model";
import {catchError, switchMap} from "rxjs/operators";
import {Observable, of} from "rxjs";
import {MatIconModule} from "@angular/material/icon";
import {TranslateService} from "@ngx-translate/core";
import * as Bowser from 'bowser';
import {MultiSelectComponent} from 'src/app/modules/design-system/multi-select/multi-select.component';
import {SelectTriggerComponent} from 'src/app/modules/design-system/multi-select/select-trigger/select-trigger.component';
import {SelectOptionComponent} from 'src/app/modules/design-system/multi-select/select-option/select-option.component';

@Component({
	selector: 'app-onboarding',
	standalone: true,
	imports: [NgOptimizedImage, MultiSelectComponent, SelectTriggerComponent, SelectOptionComponent, OnboardingStep1Component, OnboardingStep2Component, OnboardingStep3Component, OnboardingStep4Component, OnboardingStep5Component, OnboardingStep6Component, MatIconModule],
	animations: [
		trigger(
			'inOutAnimation',
			[
				transition(
					':enter',
					[
						style({ transform: 'translateY(150%)' }),
						animate('500ms 500ms ease-out',
							style({ transform: 'translateY(0)'}))
					]
				),
				transition(
					':leave',
					[
						style({ transform: 'translateY(0)' }),
						animate('500ms ease-out',
							style({ transform: 'translateY(-150%)'}))
					]
				)
			]
		)
	],
	templateUrl: './onboarding.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './onboarding.component.scss'
})
export class OnboardingComponent implements OnInit {
	private tenantService = inject(TenantService);
	private router = inject(Router);
	// Also runs outside the shell (/new-tenant, a user without workspace): never read the workspace at construction.
	private tenantStore = inject(CurrentTenantStore);
	translateService = inject(TranslateService);
	private posthogService = inject(PosthogService);
	private destroyRef = inject(DestroyRef);

	protected readonly environment = environment;

	protected readonly otherTenantsExisting = signal(false);

	protected readonly currentStep = signal('Step 1');
	secondStepForm: FormGroup;
	isMobile: boolean;

	protected readonly loading = signal(true);

	languageFormControl = new FormControl('fr');

	ngOnInit() {
		this.isMobile = Bowser.getParser(window.navigator.userAgent).getPlatform().type === PlatformType.mobile;
		this.tenantService.getTenants()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(tenants => {
				if (tenants.length > 0) {
					const currentTenant = tenants.find(tenant => tenant.configuration.isDefault);
					if (!currentTenant) {
						this.otherTenantsExisting.set(true);
					} else if (currentTenant && currentTenant.configuration.onboardingCompleted) {
						this.otherTenantsExisting.set(true);
					} else if (this.isMobile) {
						// L'étape 4 installe l'extension de navigateur : impossible sur mobile,
						// on termine l'onboarding au lieu d'y laisser l'utilisateur bloqué.
						this.onComplete();
					} else {
						this.currentStep.set('Step 4');
						this.otherTenantsExisting.set(false);
					}
				} else {
					this.otherTenantsExisting.set(false);
				}
				this.loading.set(false);
			});

		// Lang
		this.languageFormControl.setValue(this.translateService.currentLang || localStorage.getItem('lang') || 'fr');

		this.languageFormControl.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((lang) => {
				this.onChangeLang(lang as string);
			});
	}

	/** Mobile : parcours court (création de l'espace seulement), les étapes suivantes exigent l'extension de navigateur. */
	get shortOnboarding(): boolean {
		return this.otherTenantsExisting() || this.isMobile;
	}

	scrollToStep(step: string) {
		this.currentStep.set(step);
	}

	setSecondStepForm(form: FormGroup) {
		this.secondStepForm = form;
		this.currentStep.set('Step 3');
	}

	onComplete(event?: string, tenantId?: string) {
		this.posthogService.capture('onboarding_completed', {
			destination: event || 'home',
		});
		if (tenantId) {
			this.markOnboardingCompleted(tenantId)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(async () => {
					if (event === 'apps') {
						await this.router.navigate([APPLICATIONS_URL]);
						return window.location.reload();
					} else if (event) {
						this.tenantStore.initialize(event);
						await this.router.navigate([HOME_URL]);
						return window.location.reload();
					} else {
						// On recharge, comme les autres branches : le query param fait sélectionner le nouvel
						// espace au boot.
						await this.router.navigate([HOME_URL], {queryParams: {tenant: tenantId}});
						return window.location.reload();
					}
				});
		} else {
			// The workspace being onboarded: already loaded inside the shell, created by step 3 otherwise.
			this.tenantStore.whenLoaded()
				.pipe(
					switchMap(tenant => this.markOnboardingCompleted(tenant.configuration.id)),
					takeUntilDestroyed(this.destroyRef)
				).subscribe(async () => {
					if (event === 'apps') {
						await this.router.navigate([APPLICATIONS_URL]);
						return window.location.reload();
					} else if (event) {
						this.tenantStore.initialize(event);
						await this.router.navigate([HOME_URL]);
						return window.location.reload();
					} else {
						await this.router.navigate([HOME_URL]);
						return window.location.reload();
					}
				});
		}
	}

	/**
	 * Best effort, on purpose: a failure must not keep the user in the onboarding they are leaving. The flag only decides
	 * whether a later visit of the onboarding resumes at the extension step.
	 */
	private markOnboardingCompleted(tenantId: string): Observable<boolean> {
		return this.tenantService.setOnboardingCompleted(tenantId).pipe(catchError(() => of(false)));
	}

	onChangeLang(lang: string) {
		localStorage.setItem('lang', lang);
		this.translateService.use(lang);
	}

	onClose() {
		this.router.navigate([HOME_URL]);
	}

	get languageValue(): 'Français' | 'English' {
		if (this.languageFormControl.value === 'en') {
			return 'English';
		}
		return 'Français';
	}
}

enum PlatformType {
	// Retrieved from bowser project on github.com
	tablet = 'tablet',
	mobile = 'mobile',
	desktop = 'desktop',
	tv = 'tv',
	bot = 'bot',
}
